import React, { useState } from 'react';
import { Clock, CalendarX, Plus, Trash2, Copy } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { formatWa } from '../waFormat';

const DIAS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const CURTOS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

// mesma regra do backend (_resumo_horarios), para a prévia
export function resumoHorarios(hs = {}) {
  const grupos = [];
  for (let i = 0; i < 7; i++) {
    const d = hs[String(i)] || {};
    if (!d.ativo) continue;
    const g = grupos[grupos.length - 1];
    if (g && g[1] === i - 1 && g[2] === d.inicio && g[3] === d.fim) g[1] = i;
    else grupos.push([i, i, d.inicio, d.fim]);
  }
  return grupos.map(([a, b, ini, fim]) =>
    `${a === b ? CURTOS[a] : `${CURTOS[a]} ${b === a + 1 ? 'e' : 'a'} ${CURTOS[b]}`}, das ${ini} às ${fim}`).join('; ') || 'sem horário definido';
}
const fmtData = (d) => { const [a, m, dia] = (d || '').split('-'); return dia ? `${dia}/${m}${a ? `/${a}` : ''}` : d; };

export default function HorarioConfig({ cfg, up, admin }) {
  const [novo, setNovo] = useState({ data: '', nome: '', recorrente: true });
  const hs = cfg.horarios || {};
  const setDia = (i, campo, v) => up('horarios')({ ...hs, [String(i)]: { ...(hs[String(i)] || {}), [campo]: v } });
  const copiarSegunda = () => {
    const seg = hs['0'] || { ativo: true, inicio: '08:00', fim: '18:00' };
    const n = { ...hs };
    for (let i = 1; i < 5; i++) n[String(i)] = { ...seg };
    up('horarios')(n);
  };
  const feriados = cfg.feriados || [];
  const addFeriado = () => {
    if (!novo.data) return;
    up('feriados')([...feriados.filter(f => f.data !== novo.data), { ...novo, nome: novo.nome.trim() || 'Feriado' }].sort((a, b) => a.data.localeCompare(b.data)));
    setNovo({ data: '', nome: '', recorrente: true });
  };
  const resumo = resumoHorarios(hs);
  const msgFora = (cfg.msg_fora_horario || '').replaceAll('{horarios}', resumo)
    .replaceAll('{inicio}', (Object.values(hs).find(d => d?.ativo) || {}).inicio || '')
    .replaceAll('{fim}', (Object.values(hs).find(d => d?.ativo) || {}).fim || '');

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start" data-testid="wa-config-horario">
      <div className="space-y-4">
        <Card className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><Clock className="w-5 h-5 text-emerald-600" /> Horário de atendimento</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Fora do horário o cliente entra na fila normalmente, mas recebe a mensagem automática.</p>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer shrink-0">
              <input type="checkbox" checked={!!cfg.horario_ativo} onChange={e => up('horario_ativo')(e.target.checked)} disabled={!admin}
                className="w-4 h-4 accent-emerald-600" data-testid="wa-horario-ativo" />
              Ativo
            </label>
          </div>
          <fieldset disabled={!admin} className={cfg.horario_ativo ? '' : 'opacity-50'}>
            <div className="divide-y rounded-xl border">
              {DIAS.map((nome, i) => {
                const d = hs[String(i)] || {};
                return (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 flex-wrap" data-testid={`wa-horario-dia-${i}`}>
                    <label className="flex items-center gap-2 w-32 text-sm cursor-pointer">
                      <input type="checkbox" checked={!!d.ativo} onChange={e => setDia(i, 'ativo', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                      <span className={d.ativo ? 'font-medium' : 'text-muted-foreground'}>{nome}</span>
                    </label>
                    {d.ativo ? (
                      <div className="flex items-center gap-2 text-sm">
                        <Input type="time" value={d.inicio || '08:00'} onChange={e => setDia(i, 'inicio', e.target.value)} className="w-28 h-8" />
                        às
                        <Input type="time" value={d.fim || '18:00'} onChange={e => setDia(i, 'fim', e.target.value)} className="w-28 h-8" />
                        {d.ativo && d.inicio >= d.fim && <span className="text-xs text-red-600">início depois do fim</span>}
                      </div>
                    ) : <span className="text-sm text-muted-foreground">Fechado</span>}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
              <p className="text-xs text-muted-foreground">Horário reduzido: é só colocar um horário menor no dia (ex.: sábado 08:00 às 12:00).</p>
              <Button type="button" size="sm" variant="ghost" onClick={copiarSegunda}><Copy className="w-3.5 h-3.5 mr-1" /> Copiar segunda para ter–sex</Button>
            </div>
          </fieldset>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-1"><CalendarX className="w-5 h-5 text-emerald-600" /> Feriados e folgas</h3>
          <p className="text-xs text-muted-foreground mb-3">Nesses dias o atendimento é considerado fechado o dia todo. “Todo ano” repete na mesma data (ex.: Natal).</p>
          <fieldset disabled={!admin}>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <label className="text-xs font-medium">Data<Input type="date" value={novo.data} onChange={e => setNovo({ ...novo, data: e.target.value })} className="h-9 mt-1 w-40" data-testid="wa-feriado-data" /></label>
              <label className="text-xs font-medium flex-1 min-w-[160px]">Nome<Input value={novo.nome} onChange={e => setNovo({ ...novo, nome: e.target.value })} placeholder="Ex.: Natal" className="h-9 mt-1" data-testid="wa-feriado-nome" /></label>
              <label className="flex items-center gap-1.5 text-sm h-9 cursor-pointer">
                <input type="checkbox" checked={novo.recorrente} onChange={e => setNovo({ ...novo, recorrente: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Todo ano
              </label>
              <Button type="button" size="sm" variant="outline" onClick={addFeriado} disabled={!novo.data} className="h-9" data-testid="wa-feriado-add"><Plus className="w-4 h-4 mr-1" /> Adicionar</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {feriados.map(f => (
                <div key={f.data} className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm">
                  <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5">{f.recorrente ? fmtData(f.data).slice(0, 5) : fmtData(f.data)}</span>
                  <span className="flex-1 truncate">{f.nome}</span>
                  {f.recorrente && <span className="text-[10.5px] text-muted-foreground">todo ano</span>}
                  <button type="button" onClick={() => up('feriados')(feriados.filter(x => x.data !== f.data))} className="text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {!feriados.length && <p className="text-sm text-muted-foreground">Nenhum feriado cadastrado.</p>}
            </div>
          </fieldset>
        </Card>

        <Card className="p-5 space-y-3">
          <h3 className="font-semibold">Mensagens automáticas</h3>
          <fieldset disabled={!admin} className="space-y-3">
            <label className="block text-sm font-medium">Fora do horário
              <textarea rows={3} value={cfg.msg_fora_horario || ''} onChange={up('msg_fora_horario')} className="mt-1 w-full border rounded-md p-2 text-sm bg-background font-normal" data-testid="wa-msg-fora" />
            </label>
            <label className="block text-sm font-medium">Em feriado
              <textarea rows={3} value={cfg.msg_feriado || ''} onChange={up('msg_feriado')} className="mt-1 w-full border rounded-md p-2 text-sm bg-background font-normal" />
            </label>
            <p className="text-xs text-muted-foreground">
              Use <code>{'{horarios}'}</code> para o resumo dos horários e <code>{'{feriado}'}</code> para o nome do feriado.
            </p>
          </fieldset>
        </Card>
      </div>

      <Card className="p-4 lg:sticky lg:top-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Cliente chama fora do horário</p>
        <div className="rounded-xl p-3 space-y-2" style={{ background: '#efeae2' }}>
          <div className="bg-white rounded-lg px-3 py-2 text-[13px] w-fit text-slate-900">Boa noite, preciso de ajuda</div>
          <div className="bg-[#d9fdd3] rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap ml-auto max-w-[92%] text-slate-900">{formatWa(`🕐 ${msgFora}`)}</div>
        </div>
        <p className="text-xs text-muted-foreground mt-3"><b>Resumo:</b> {resumo}</p>
      </Card>
    </div>
  );
}
